import {FunctionComponent, MouseEvent, TableHTMLAttributes, useCallback, useMemo} from 'react';
import {clsx} from 'clsx';
import {twMerge} from 'tailwind-merge';
import ExclamationPointIcon from '@/assets/images/icon-exclamation-point.svg?react';
import Button from '@/components/button/button';
import {TrackingKeyVariants} from '@/components/tracked-atoms/types';
import {BUTTON_THEME, BUTTON_VARIANT, ButtonTheme} from '@/constants/button';
import {TABLE_THEME, TableTheme} from '@/constants/table';

export type TableRow = {
  rowId?: string;
  dataTestid?: string;
  columns: string[];
  onClick?: () => void;
  disabled?: boolean;
  actionButton?: {
    label: string;
    theme?: ButtonTheme;
    trackingKey: TrackingKeyVariants;
  };
  isError?: boolean;
};

type BodyProps = TableHTMLAttributes<HTMLTableCellElement> & {
  row: TableRow;
  theme: TableTheme;
};

const TableBodyRow: FunctionComponent<BodyProps> = ({row: {isError = false, ...row}, theme}) => {
  const isPrimary = theme === TABLE_THEME.PRIMARY;

  const showError = isError && !!row.columns[0] && row.columns[0]?.length > 0;

  const classNames = useMemo((): string => {
    switch (theme) {
      case TABLE_THEME.PRIMARY:
        return twMerge(
          clsx(
            'bg-grey-100 py-3 transition-colors first:rounded-l-2 first:pl-3 last:rounded-r-2 last:pr-3',
            row.onClick && !row.disabled && 'cursor-pointer group-hover:bg-purple-100',
            row.disabled && 'bg-grey-200 text-grey-675',
            showError && 'bg-red-100',
          ),
        );
      case TABLE_THEME.SECONDARY:
        return twMerge(
          clsx(
            'border-t border-grey-400 bg-transparent py-4 font-normal text-charcoal first:pl-3 last:pr-3',
            row.onClick && !row.disabled && 'cursor-pointer group-hover:bg-grey-100',
            row.disabled && 'bg-grey-200 text-grey-650',
            showError && 'bg-red-100',
          ),
        );
    }
  }, [theme, row.onClick, row.disabled, showError]);

  const handleActionButtonClick = useCallback(
    (event: MouseEvent): void => {
      event.stopPropagation();
      row.onClick?.();
    },
    [row],
  );

  return (
    <>
      <tr id={row.rowId} data-testid={row.dataTestid} className={'group'} onClick={row.onClick}>
        {row.columns.map((label, index) => (
          <td key={index} className={classNames}>
            <div className="flex items-center">
              {index === 0 && showError && (
                <ExclamationPointIcon
                  data-testid="table-row-error-icon"
                  className="mr-2 size-4 shrink-0 rounded-full bg-red fill-white"
                />
              )}
              {label}
            </div>
          </td>
        ))}
        {row.actionButton && (
          <td className={clsx(classNames, 'w-0 whitespace-nowrap')}>
            <Button
              className="w-full"
              theme={row.actionButton.theme ?? BUTTON_THEME.SECONDARY}
              variant={BUTTON_VARIANT.X_SMALL}
              label={row.actionButton.label}
              trackingKey={row.actionButton.trackingKey}
              onClick={row.disabled ? undefined : handleActionButtonClick}
              disabled={row.disabled}
            />
          </td>
        )}
      </tr>

      {isPrimary && (
        <tr data-testid="table-spacer-row">
          <td className="p-1"></td>
        </tr>
      )}
    </>
  );
};

export default TableBodyRow;
